import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { ContentView, EnrichedContent } from '../../../../api/client'
import { getClassifiersToDisplay } from '../../../../util/classifierListUtils'
import Button from '../../../Button'
import Modal from '../../../Modal'
import ContentMetadataAuthor from './ContentMetadataAuthor'
import ContentMetadataClassification from './ContentMetadataClassification'
import ContentMetadataDetails from './ContentMetadataDetails'
import ContentMetadataPostDetails from './ContentMetadataPostDetails'
import { downloadMetadataAsText } from './formatMetadataAsText'

type Props = {
    open: boolean
    setOpen: (open: boolean) => void
    content: ContentView
    enrichedContent: EnrichedContent | undefined
}

type RowProps = {
    title: string
    children: React.ReactNode
    zebra_dark: boolean
}

/**
 * A single, zebra-striped row in the content metadata modal.
 * Just a place to put all the CSS really.
 *
 * Use like this:
 * ```
 *   <ContentMetadataRow
 *      title="Account ID:"
 *      zebra_dark={true}
 *      children={<ProfileId content={content} />}
 *  />
 * ```
 */
export function ContentMetadataRow({ title, children, zebra_dark }: RowProps) {
    const rowColour = zebra_dark ? 'bg-ink-50' : 'bg-white'
    return (
        <div className={`${rowColour} px-4 py-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0`}>
            <dt className="text-sm font-medium leading-6 text-ink-900">{title}</dt>
            <dd className="mt-1 text-sm leading-6 text-ink-700 sm:col-span-2 sm:mt-0">
                {children}
            </dd>
        </div>
    )
}

export function SectionHeader({ title }: { title: string }) {
    return (
        <div className="px-4 sm:px-0">
            <h3 className="font-sans !font-medium text-base leading-7">{title}</h3>
        </div>
    )
}

/**
 * Modal that display content details.
 */
export default function ContentMetadataModal(props: Props) {
    const { open, setOpen, content, enrichedContent } = props

    const classifiersToDisplay = getClassifiersToDisplay(content)

    // Combine severe and suspect for download
    const contributingClassifications = [
        ...classifiersToDisplay.relevantSevereClassifications,
        ...classifiersToDisplay.relevantSuspectClassifications
    ]

    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            title="Content metadata"
            icon={<InformationCircleIcon className="h-8 w-8 text-ink-700" aria-hidden="true" />}
            size="fit"
            footer={
                <>
                    <Button text="Close" primary={false} onClick={() => setOpen(false)} />
                    <Button
                        text="Download"
                        primary={true}
                        onClick={() =>
                            downloadMetadataAsText({
                                content,
                                enrichedContent,
                                moderation: content.moderation,
                                classifications: contributingClassifications
                            })
                        }
                    />
                </>
            }
        >
            <div className="overflow-y-scroll max-h-[50vh] min-w-[750px] space-y-4">
                <ContentMetadataAuthor content={content} />
                <ContentMetadataDetails enrichedContent={enrichedContent} content={content} />
                <ContentMetadataPostDetails content={content} />
                <ContentMetadataClassification
                    moderation={content.moderation}
                    severeClassifications={classifiersToDisplay.relevantSevereClassifications}
                    suspectClassifications={classifiersToDisplay.relevantSuspectClassifications}
                    accountId={content.account.id}
                    contentId={content.id}
                />
            </div>
        </Modal>
    )
}
